/**
 * AuditRuleCard Types
 *
 * Presentation-layer types for the redesigned JotAudit compliance cards.
 * The card is a pure presentation component — Phase 2 container maps
 * API types (RuleComplianceAssessment + ComplianceRuleDocument) to this shape.
 */

// ---------------------------------------------------------------------------
// Enums / Unions
// ---------------------------------------------------------------------------

/** Card lifecycle state. */
export type AuditCardStatus = 'idle' | 'updating' | 'interrupted' | 'completed' | 'ignored';

/** Card variant — determines primary button label and data expectations. */
export type AuditCardVariant = 'rule' | 'cpt';

/** Compliance severity — drives status label, colors, and button style. */
export type AuditCardSeverity = 'non_compliant' | 'warning';

// ---------------------------------------------------------------------------
// Data
// ---------------------------------------------------------------------------

/** Source document info (from ComplianceRuleDocument.source). */
export interface AuditCardSourceInfo {
  documentTitle?: string | null;
  documentDate?: string | null;
  sourceUri?: string | null;
}

/** Rule applicability facets (from ComplianceRuleDocument.facets). */
export interface AuditCardFacets {
  cpt?: string[];
  payer?: string[];
  providerType?: string[];
}

/**
 * Flat data shape consumed by the card.
 * Intentionally decoupled from API types so the card stays a pure presentation
 * component that can be tested in Storybook with simple mock objects.
 */
export interface AuditRuleCardData {
  ruleId: string;
  ruleTitle: string;
  severity: AuditCardSeverity;
  variant: AuditCardVariant;
  /** AI-generated explanation of WHY the rule is non-compliant. */
  reason: string;
  /** AI-generated suggestion for how to fix it. */
  suggestion?: string | null;
  /** Note section IDs affected by this rule. */
  relevantSections?: string[] | null;
  /** Source document info shown on the collapsed card surface. */
  source?: AuditCardSourceInfo | null;
  /** Full rule description in Markdown (expanded content). */
  bodyMd?: string | null;
  /** Applicability facets (expanded content). */
  facets?: AuditCardFacets | null;
  /** For CPT variant: the AI-suggested CPT code. */
  suggestedCptCode?: string | null;
  /** For CPT variant: the current/billed CPT code. */
  currentCptCode?: string | null;
  /** For CPT variant: human-readable description of the proposed code. */
  cptDescription?: string | null;
}

// ---------------------------------------------------------------------------
// Interrupt handlers (Phase 4 — inline fix)
// ---------------------------------------------------------------------------

/** Callbacks for resolving AI interrupts on the card surface. */
export interface InterruptHandlers {
  onConfirm: (confirmed: boolean, skipCacheUpdate?: boolean) => void;
  onContinue: (
    selectedIds: string[] | Record<string, { selected_ids?: string[]; confirmed?: boolean }>,
  ) => void;
  onMultiStepComplete: (
    responses: Record<string, { selected_ids?: string[]; confirmed?: boolean }>,
  ) => void;
  onExpressInterest: (interested: boolean) => void;
  onDismiss: () => void;
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

/** How a card reached the 'completed' state. */
export type CardCompletedVia = 'ai' | 'manual';

export interface AuditRuleCardProps {
  /** Card data — merged from audit assessment + rule definition. */
  data: AuditRuleCardData;
  /** Current visual/lifecycle state of the card. */
  status: AuditCardStatus;
  /** How the card was completed — affects completed-state label text. */
  completedVia?: CardCompletedVia;
  /** Whether the current user is an admin (affects overflow menu items). */
  isAdmin?: boolean;

  // --- Action callbacks ---
  /** Primary action: "Approve AI suggestion" / "Apply suggestion" / "Update CPT". */
  onApprove?: () => void;
  /** Whether the primary action button is disabled (e.g., another card is updating). */
  disableApprove?: boolean;
  /** Secondary action: "Ignore" / "Dismiss". */
  onIgnore?: () => void;
  /** Revert a completed or ignored action. */
  onUndo?: () => void;
  /** User manually resolved this rule (overflow menu action). */
  onMarkResolved?: () => void;

  // --- Overflow menu callbacks ---
  /** Admin: navigate to rule builder. */
  onEditRule?: () => void;
  /** Admin: navigate to rule analytics page. */
  onViewAnalytics?: () => void;
  /** Admin: disable/enable the rule. */
  onDisableRule?: () => void;
  /** All users: expand card or link to source. Only rendered if provided. */
  onLearnMore?: () => void;

  /** Scroll note editor to a specific section. */
  onSectionClick?: (sectionId: string) => void;

  // --- Expand mode ---
  /** How expanded content is displayed: inline within card or via modal. Default: 'inline'. */
  expandMode?: 'inline' | 'modal';
  /** Called when expandMode is 'modal' and user clicks the expand trigger. */
  onExpandModal?: () => void;

  // --- Interrupt (Phase 4) ---
  /** Content to render when the card is in 'interrupted' state (QuickReplyArea / MultiStepWizard). */
  interruptContent?: React.ReactNode;
  /** Called when the user cancels a fix during an interrupted state. */
  onCancelFix?: () => void;

  // --- Change summary (completed cards) ---
  /** AI's description of what changed during the fix. Null for manual completions. */
  changeSummary?: string | null;
  /** Whether undo is currently available. Drives expanded vs collapsed summary treatment. */
  canUndo?: boolean;
}

// ---------------------------------------------------------------------------
// Status config
// ---------------------------------------------------------------------------

export interface AuditStatusConfig {
  /** Badge pill text. */
  badgeText: string;
  /** Badge pill background color. */
  badgeBgColor: string;
  /** Badge pill text color. */
  badgeTextColor: string;
  /** Primary button label (e.g., "✦ Approve AI suggestion"). */
  buttonLabel: string;
  /** Primary button MUI variant. */
  buttonVariant: 'contained' | 'outlined';
  /** Secondary action label (e.g., "Ignore" or "Dismiss"). */
  secondaryLabel: string;
}

/**
 * Returns the status configuration for an idle/updating/interrupted card.
 * Pure function — no side effects.
 */
export function getStatusConfig(
  variant: AuditCardVariant,
  severity: AuditCardSeverity,
  suggestedCptCode?: string | null,
): AuditStatusConfig {
  if (variant === 'cpt') {
    return {
      badgeText: 'CPT code mismatch',
      badgeBgColor: '#FCEBEB',
      badgeTextColor: '#791F1F',
      buttonLabel: suggestedCptCode ? `✦ Update CPT to ${suggestedCptCode}` : '✦ Update CPT code',
      buttonVariant: 'contained',
      secondaryLabel: 'Ignore',
    };
  }

  if (severity === 'warning') {
    return {
      badgeText: 'Improvement suggested',
      badgeBgColor: '#FAEEDA',
      badgeTextColor: '#633806',
      buttonLabel: '✦ Fix it for me',
      buttonVariant: 'outlined',
      secondaryLabel: 'Dismiss',
    };
  }

  // Default: non-compliant rule
  return {
    badgeText: 'Rule non-compliant',
    badgeBgColor: '#FCEBEB',
    badgeTextColor: '#791F1F',
    buttonLabel: '✦ Fix it for me',
    buttonVariant: 'contained',
    secondaryLabel: 'Ignore',
  };
}

/** Badge config for completed cards. */
export function getCompletedBadgeConfig(completedVia: 'ai' | 'manual') {
  return {
    text: completedVia === 'manual' ? 'Rule resolved' : 'Rule edited with JotAudit',
    bgColor: '#EAF3DE',
    textColor: '#27500A',
  };
}

/** Badge config for ignored cards. */
export function getIgnoredBadgeConfig() {
  return {
    text: 'Rule ignored',
    bgColor: '#F5F5F5',
    textColor: '#757575',
  };
}
